import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FavoriteButton = ({ listingId, size = 'default', showCount = false, className = '' }) => {
  const { user, token } = useAuth();
  const [isFavorited, setIsFavorited] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bouncing, setBouncing] = useState(false);

  useEffect(() => {
    checkFavoriteStatus();
    if (showCount) {
      fetchCount();
    }
  }, [listingId, user]);

  const checkFavoriteStatus = async () => {
    if (!user || !token) {
      // Check localStorage for non-logged in users
      const localFavorites = (() => { try { return JSON.parse(localStorage.getItem('favorites') || '[]'); } catch { return []; } })();
      setIsFavorited(localFavorites.includes(listingId));
      return;
    }

    try {
      const response = await axios.get(`${API}/favorites/check/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorited(response.data.favorited);
    } catch (error) {
      // Fallback to localStorage
      const localFavorites = (() => { try { return JSON.parse(localStorage.getItem('favorites') || '[]'); } catch { return []; } })();
      setIsFavorited(localFavorites.includes(listingId));
    }
  };

  const fetchCount = async () => {
    try {
      const response = await axios.get(`${API}/favorites/count/${listingId}`);
      setCount(response.data.count);
    } catch (error) {
      console.error('Error fetching favorite count:', error);
    }
  };

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (loading) return;
    setLoading(true);

    try {
      if (!user || !token) {
        // Handle localStorage for non-logged in users
        const localFavorites = (() => { try { return JSON.parse(localStorage.getItem('favorites') || '[]'); } catch { return []; } })();
        if (isFavorited) {
          const newFavorites = localFavorites.filter(id => id !== listingId);
          localStorage.setItem('favorites', JSON.stringify(newFavorites));
          setIsFavorited(false);
        } else {
          localFavorites.push(listingId);
          localStorage.setItem('favorites', JSON.stringify(localFavorites));
          setIsFavorited(true);
        }
      } else {
        // API call for logged in users
        const response = await axios.post(`${API}/favorites/toggle`, 
          { listing_id: listingId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsFavorited(response.data.favorited);
        
        if (showCount) {
          setCount(prev => response.data.favorited ? prev + 1 : prev - 1);
        }
      }
      // Trigger bounce animation
      setBouncing(true);
      setTimeout(() => setBouncing(false), 400);
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-10 h-10',
    large: 'w-12 h-12'
  };

  const iconSizes = {
    small: 'w-4 h-4',
    default: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  return (
    <button
      onClick={toggleFavorite}
      disabled={loading}
      className={`
        ${sizeClasses[size]}
        flex items-center justify-center rounded-full
        transition-all duration-200 ease-out
        ${isFavorited 
          ? 'bg-red-500 text-white hover:bg-red-600' 
          : 'bg-black/50 text-white hover:bg-black/70 backdrop-blur-sm'
        }
        ${loading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
        ${className}
      `}
      title={isFavorited ? 'Remove from Favorites' : 'Add to Favorites'}
    >
      <Heart
        className={`${iconSizes[size]} transition-transform ${loading ? 'scale-90' : ''} ${bouncing ? 'animate-heart-bounce' : ''}`}
        fill={isFavorited ? 'currentColor' : 'none'}
        strokeWidth={2}
      />
      {showCount && count > 0 && (
        <span className="ml-1 text-xs font-medium">{count}</span>
      )}
    </button>
  );
};

export default FavoriteButton;
